import { useCallback, useMemo, useState } from 'react';
import type {
  LessonBlockTranslationRecord,
  TranslationReplacementAction,
} from '@course-authoring/shared-types';
import { TranslationReplacementPrompt } from './TranslationStatusPrimitives';

export interface BlockTranslationDecision {
  action: TranslationReplacementAction;
  committed: boolean;
}

interface UseBlockTranslationReplacementPromptOptions {
  record?: LessonBlockTranslationRecord;
  decision?: BlockTranslationDecision;
  onDecision?: (action: TranslationReplacementAction) => void;
  onSourceChange?: () => void;
}

export function useBlockTranslationReplacementPrompt({
  record,
  decision,
  onDecision,
  onSourceChange,
}: UseBlockTranslationReplacementPromptOptions) {
  const [promptRequest, setPromptRequest] = useState<'hidden' | 'initial' | 'revision'>('hidden');
  const canOfferPrompt = Boolean(
    record
    && record.summary.humanTranslationCount > 0
    && !decision?.committed
    && record.summary.pendingHumanTranslationCount === 0,
  );

  const handleSourceChange = useCallback(() => {
    if (!onSourceChange) return;
    if (!record || canOfferPrompt) setPromptRequest('initial');
    onSourceChange();
  }, [canOfferPrompt, onSourceChange, record]);

  const prompt = useMemo(() => {
    const hasHumanTranslations = Boolean(record && record.summary.humanTranslationCount > 0);
    const shouldShowPrompt = promptRequest === 'revision'
      ? hasHumanTranslations
      : promptRequest === 'initial' && canOfferPrompt;
    if (!record || !(
      shouldShowPrompt
      || decision
      || record.summary.pendingHumanTranslationCount > 0
    )) return null;

    const view = shouldShowPrompt
      ? 'prompt'
      : decision
        ? decision.committed
          ? decision.action === 'preserve' ? 'preserved' : 'queued'
          : 'prompt'
        : 'queued';

    return (
      <div style={{ marginBottom: 16 }}>
        <TranslationReplacementPrompt
          humanTranslationCount={record.summary.humanTranslationCount}
          subject="block"
          view={view}
          onQueue={() => {
            setPromptRequest('hidden');
            onDecision?.('queue');
          }}
          onPreserve={() => {
            setPromptRequest('hidden');
            onDecision?.('preserve');
          }}
          onChange={() => setPromptRequest('revision')}
        />
      </div>
    );
  }, [canOfferPrompt, decision, onDecision, promptRequest, record]);

  return {
    prompt,
    handleSourceChange,
  };
}
